/* eslint-disable react/no-unstable-nested-components */
import * as React from "react";
import { useParams } from "react-router-dom";
import { useIsMutating } from "@tanstack/react-query";
import { Dialog, DialogContent, Button, Card } from "@jhool-io/fe-components";
import AddInsurance from "./AddInsurance/AddInsurance";
import { SupportedUploadMethod } from "../../../../utils/types/filepicker";
import { UserPermisions } from "../../../../utils/types/user";
import { useDoesUserHavePermission } from "../../../../hooks/permissions";
import InsuranceCardDocuments from "./InsuranceCardDocuments/InsuranceCardDocuments";
import Progress from "../../../../components/Progress/Progress";
import FilePicker from "../../../../components/FilePicker/FilePicker";
import { InsuranceTabSubModals } from "../../types/clients.types";
import { useFetchInsuranceProviders } from "../../hooks/clients.queries";
import ClientInsuranceRecords from "../ClientInsuranceRecords/ClientInsuranceRecords";

enum AddInsuranceForms {
    ADD_INSURANCE = "add-insurance-form",
    UPLOAD_DOCUMENT = "upload-document",
}

type InsuranceTabModals =
    | "add-card"
    | "add-insurance"
    | "view-insurance"
    | "delete-insurance"
    | "delete-insurance-bulk";
type SubmitTypes = "save" | "save-continue";

export default function Insurance() {
    // Component local states
    const [modalInView, setModalInView] =
        React.useState<InsuranceTabModals | null>(null);
    const [addInsuranceModalInView, setAddInsuranceModalInView] =
        React.useState<AddInsuranceForms>(AddInsuranceForms.ADD_INSURANCE);
    const [submitType, setSubmitType] = React.useState<SubmitTypes | null>(
        null
    );

    const [progressStep, setProgressStep] = React.useState<
        AddInsuranceForms | InsuranceTabSubModals
    >(AddInsuranceForms.ADD_INSURANCE);

    // Get id from url params
    const params = useParams();
    const clientId = params.clientId as string;

    // check if logged in user can add insurance
    const canAddInsurance = useDoesUserHavePermission([
        UserPermisions.CLIENT_INFO_ALL_ADMIN,
        UserPermisions.CLIENT_INFO_INSURANCE_WRITE,
        UserPermisions.CLIENT_INFO_ALL_WRITE,
    ]);

    // Hook for fetching insurance providers
    useFetchInsuranceProviders();

    // To determine if mutation is currently running, returns 0 || 1
    const addInsuranceIsMutating = useIsMutating(["add-insurance", clientId]);

    // Function to set modal in view
    const handleSetModalInView = (modal: InsuranceTabModals | null) => {
        setModalInView(modal);
    };

    return (
        <>
            <Dialog open={modalInView === "add-insurance"}>
                <DialogContent
                    showFooter={
                        addInsuranceModalInView === "add-insurance-form"
                    }
                    variant="center"
                    title={
                        addInsuranceModalInView === "add-insurance-form"
                            ? "Add insurance"
                            : "Upload insurance document"
                    }
                    handleCloseDialog={() => handleSetModalInView(null)}
                    saveText="Save and continue"
                    cancelText="Save changes"
                    canCancelButtonSubmit
                    submitBtnFormValue="add-insurance"
                    cancelBtnFormValue="add-insurance"
                    isCancelBtnDisabled={addInsuranceIsMutating > 0}
                    isSubmitBtnDisabled={addInsuranceIsMutating > 0}
                    onSaveClick={() => {
                        setSubmitType("save-continue");
                    }}
                    onCancelClick={() => setSubmitType("save")}
                >
                    <div>
                        <Progress
                            steps={Object.values(AddInsuranceForms)}
                            currentStep={progressStep}
                            className="top-12"
                        />
                        <div className="pt-5">
                            {addInsuranceModalInView ===
                                AddInsuranceForms.ADD_INSURANCE && (
                                <AddInsurance
                                    onFormSubmit={() => {
                                        if (submitType === "save-continue") {
                                            setProgressStep(
                                                AddInsuranceForms.UPLOAD_DOCUMENT
                                            );
                                            setAddInsuranceModalInView(
                                                AddInsuranceForms.UPLOAD_DOCUMENT
                                            );
                                        } else handleSetModalInView(null);
                                    }}
                                />
                            )}
                            {addInsuranceModalInView ===
                                AddInsuranceForms.UPLOAD_DOCUMENT && (
                                <FilePicker
                                    name="filepicker"
                                    uploadMethods={[
                                        {
                                            method: SupportedUploadMethod.manualupload,
                                        },
                                        {
                                            method: SupportedUploadMethod.googledrive,
                                        },
                                        {
                                            method: SupportedUploadMethod.dropbox,
                                        },
                                    ]}
                                    uploadType="insurance-file"
                                    insuranceId={
                                        localStorage.getItem(
                                            "newInsuranceId"
                                        ) as string
                                    }
                                    successCallback={() => {
                                        setModalInView(null);
                                    }}
                                />
                            )}
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            <Card classNames="flex justify-between items-center mb-[--spacing-header] gap-[--spacing-items] flex-wrap">
                <span className=" text-base font-bold">
                    Insurance Provider History
                </span>
                {canAddInsurance && (
                    <Button
                        aria-label="addInsuranceButton"
                        onClick={() => handleSetModalInView("add-insurance")}
                    >
                        Add insurance
                    </Button>
                )}
            </Card>

            <ClientInsuranceRecords />

            <div className="py-7">
                <InsuranceCardDocuments />
            </div>
        </>
    );
}
